import type {} from '@mui/lab/themeAugmentation';
import { outlinedInputClasses } from '@mui/material';
import { createTheme, extendTheme, Theme } from '@mui/material/styles';
import { TypographyStyleOptions, TypographyVariantsOptions } from '@mui/material/styles/createTypography';
import { CSSProperties } from 'react';

declare module '@mui/material/styles' {
  interface PaletteOptions {
    other: {
      component1: string;
      component2: string;
      component3: string;
      pageBackground: string;
      plainWhite: string;
      outlines: string;
      disabled: string;
      darkBlueGradient: string;
      linearWhiteGradient: string;
    };
  }

  interface TypographyVariants {
    h1: CSSProperties;
    h2: CSSProperties;
    h3: CSSProperties;
    h4: CSSProperties;
    h5: CSSProperties;
    h6: CSSProperties;

    'h1-bold': CSSProperties;
    'h2-bold': CSSProperties;
    'h3-bold': CSSProperties;
    'h4-bold': CSSProperties;
    'h5-bold': CSSProperties;
    'h6-bold': CSSProperties;

    'body/large': CSSProperties;
    'body/regular': CSSProperties;
    'body/regular-underlined': CSSProperties;
    'body/regular-bold': CSSProperties;
    'body/small': CSSProperties;
    'body/small-bold': CSSProperties;
    'body/extra-small': CSSProperties;
    'body/extra-small-bold': CSSProperties;

    'btn-label/big-light': CSSProperties;
    'btn-label/big-bold': CSSProperties;
    'btn-label/regular-light': CSSProperties;
    'btn-label/regular-light-underlined': CSSProperties;
    'btn-label/regular-light-strike-through': CSSProperties;
    'btn-label/regular-bold': CSSProperties;
    'btn-label/small-light': CSSProperties;
    'btn-label/small-light-underlined': CSSProperties;
    'btn-label/small-light-strike-through': CSSProperties;
    'btn-label/small-bold': CSSProperties;
    'btn-label/extra-small': CSSProperties;
    'btn-label/extra-small-bold': CSSProperties;

    'table/cell-value': CSSProperties;
    'table/cell-value-bold': CSSProperties;
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    h1: TypographyStyleOptions;
    h2: TypographyStyleOptions;
    h3: TypographyStyleOptions;
    h4: TypographyStyleOptions;
    h5: TypographyStyleOptions;
    h6: TypographyStyleOptions;

    'h1-bold': TypographyStyleOptions;
    'h2-bold': TypographyStyleOptions;
    'h3-bold': TypographyStyleOptions;
    'h4-bold': TypographyStyleOptions;
    'h5-bold': TypographyStyleOptions;
    'h6-bold': TypographyStyleOptions;

    'body/large': TypographyStyleOptions;
    'body/regular': TypographyStyleOptions;
    'body/regular-underlined': TypographyStyleOptions;
    'body/regular-bold': TypographyStyleOptions;
    'body/small': TypographyStyleOptions;
    'body/small-bold': TypographyStyleOptions;
    'body/extra-small': TypographyStyleOptions;
    'body/extra-small-bold': TypographyStyleOptions;

    'btn-label/big-light': TypographyStyleOptions;
    'btn-label/big-bold': TypographyStyleOptions;
    'btn-label/regular-light': TypographyStyleOptions;
    'btn-label/regular-light-underlined': TypographyStyleOptions;
    'btn-label/regular-light-strike-through': TypographyStyleOptions;
    'btn-label/regular-bold': TypographyStyleOptions;
    'btn-label/small-light': TypographyStyleOptions;
    'btn-label/small-light-underlined': TypographyStyleOptions;
    'btn-label/small-light-strike-through': TypographyStyleOptions;
    'btn-label/small-bold': TypographyStyleOptions;
    'btn-label/extra-small': TypographyStyleOptions;
    'btn-label/extra-small-bold': TypographyStyleOptions;

    'table/cell-value': TypographyStyleOptions;
    'table/cell-value-bold': TypographyStyleOptions;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    h1: true;
    h2: true;
    h3: true;
    h4: true;
    h5: true;
    h6: true;

    'h1-bold': true;
    'h2-bold': true;
    'h3-bold': true;
    'h4-bold': true;
    'h5-bold': true;
    'h6-bold': true;

    'body/large': true;
    'body/regular': true;
    'body/regular-underlined': true;
    'body/regular-bold': true;
    'body/small': true;
    'body/small-bold': true;
    'body/extra-small': true;
    'body/extra-small-bold': true;

    'btn-label/big-light': true;
    'btn-label/big-bold': true;
    'btn-label/regular-light': true;
    'btn-label/regular-light-underlined': true;
    'btn-label/regular-light-strike-through': true;
    'btn-label/regular-bold': true;
    'btn-label/small-light': true;
    'btn-label/small-light-underlined': true;
    'btn-label/small-light-strike-through': true;
    'btn-label/small-bold': true;
    'btn-label/extra-small': true;
    'btn-label/extra-small-bold': true;

    'table/cell-value': true;
    'table/cell-value-bold': true;
  }
}

export const fontFamily = ['Inter', 'sans-serif'].join(',');
export const tableFontFamily = ['Roboto', 'sans-serif'].join(',');

export const fontSizeRegular = 16;

// Override theme shadows
const defaultTheme = createTheme();
const shadows = defaultTheme.shadows;
shadows[1] = '0px 0px 8px rgba(0, 0, 0, 0.04)';
shadows[2] = '0px -2px 22px rgba(18, 27, 33, 0.07)';

// Colors

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const background = {
  default: '#EAEDF7',
  paper: '#FFFFFF',
};

const primary = {
  dark: '#000061',
  main: '#00007B',
  light: '#333396',
};

const text = {
  highContrast: '#00112C',
  mediumContrast: '#595D6B',
  lowContrast: '#9D9BAA',
};

const success = {
  dark: '#007C23',
  main: '#37D400',
  light: '#E7F1E8',
};

const warning = {
  dark: '#473500',
  main: '#674E01',
  light: '#FAF4D4',
};

const error = {
  dark: '#C30013',
  main: '#FF6E6E',
  light: '#F8E0E0',
};

const info = {
  dark: '#003EB7',
  main: '#5D7189',
  light: '#E0E8F8',
};

const other = {
  component1: '#FFFFFF',
  component2: '#F3F3F8',
  component3: '#E5EAFF',
  pageBackground: '#EAEDF7',
  plainWhite: '#FFFFFF',
  outlines: '#DDDDDD',
  disabled: '#DADADA',
  darkBlueGradient: 'linear-gradient(180deg, #0000B7 0%, #00007B 100%)',
  linearWhiteGradient: 'linear-gradient(135deg, #F9F9FA 0%, #F0F2F9 100%)',
  textOnDarkHighContrast: '#FFFFFF',
  textOnDarkMediumContrast: '#B2B2D7',
};

const borderRadius = 8;
const spacing = 8;

export const typographyStyles: TypographyVariantsOptions = {
  fontFamily, // Default font-family
  fontSize: 16, // Default font-size
  h1: {
    fontFamily,
    fontSize: 36,
    fontWeight: '400',
    lineHeight: 1.3,
  },
  h2: {
    fontFamily,
    fontSize: 32,
    fontWeight: '400',
    lineHeight: 1.3,
  },
  h3: {
    fontFamily,
    fontSize: 28,
    fontWeight: '400',
    lineHeight: 1.3,
  },
  h4: {
    fontFamily,
    fontSize: 24,
    fontWeight: '400',
    lineHeight: 1.3,
  },
  h5: {
    fontFamily,
    fontSize: 18,
    fontWeight: '400',
    lineHeight: 1.5,
  },
  h6: {
    fontFamily,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 1.5,
  },

  'h1-bold': {
    fontFamily,
    fontSize: 36,
    fontWeight: '600',
    lineHeight: 1.3,
  },
  'h2-bold': {
    fontFamily,
    fontSize: 32,
    fontWeight: '600',
    lineHeight: 1.3,
  },
  'h3-bold': {
    fontFamily,
    fontSize: 28,
    fontWeight: '600',
    lineHeight: 1.3,
  },
  'h4-bold': {
    fontFamily,
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 1.3,
  },
  'h5-bold': {
    fontFamily,
    fontSize: 18,
    fontWeight: '600',
    lineHeight: 1.5,
  },
  'h6-bold': {
    fontFamily,
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 1.5,
  },

  'body/large': {
    fontFamily,
    fontSize: 18,
    fontWeight: '400',
    lineHeight: 1.5,
  },
  'body/regular': {
    fontFamily,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 1.5,
  },
  'body/regular-underlined': {
    fontFamily,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 1.5,
  },
  'body/regular-bold': {
    fontFamily,
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 1.5,
  },
  'body/small': {
    fontFamily,
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 1.5,
  },
  'body/small-bold': {
    fontFamily,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 1.5,
    color: text.highContrast,
  },
  'body/extra-small': {
    fontFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 1.5,
  },
  'body/extra-small-bold': {
    fontFamily,
    fontSize: 12,
    fontWeight: '600',
    lineHeight: 1.5,
  },

  'btn-label/big-light': { fontFamily, fontSize: 18, fontWeight: '500', lineHeight: 1.06 },
  'btn-label/big-bold': { fontFamily, fontSize: 18, fontWeight: '600', lineHeight: 1.06 },
  'btn-label/regular-light': { fontFamily, fontSize: 16, fontWeight: '500' },
  'btn-label/regular-light-underlined': {
    fontFamily,
    fontSize: 16,
    fontWeight: '400',
    textDecorationLine: 'underline',
  },
  'btn-label/regular-light-strike-through': {
    fontFamily,
    fontSize: 16,
    fontWeight: '400',
    textDecorationLine: 'line-through',
  },
  'btn-label/regular-bold': { fontFamily, fontSize: 16, fontWeight: '600' },
  'btn-label/small-light': { fontFamily, fontSize: 14, fontWeight: '400' },
  'btn-label/small-light-underlined': {
    fontFamily,
    fontSize: 14,
    fontWeight: '400',
    textDecorationLine: 'underline',
  },
  'btn-label/small-light-strike-through': {
    fontFamily,
    fontSize: 14,
    fontWeight: '400',
    textDecorationLine: 'line-through',
  },
  'btn-label/small-bold': { fontFamily, fontSize: 14, fontWeight: '600' },
  'btn-label/extra-small': { fontFamily, fontSize: 12, fontWeight: '400' },
  'btn-label/extra-small-bold': { fontFamily, fontSize: 12, fontWeight: '600 ' },

  'table/cell-value': { fontFamily: tableFontFamily, fontSize: 14, fontWeight: '400', lineHeight: 1.3 },
  'table/cell-value-bold': { fontFamily: tableFontFamily, fontSize: 14, fontWeight: '500', lineHeight: 1.3 },
};

export const createAppTheme = (isDarkMode: boolean): Theme =>
  createTheme({
    shadows,
    spacing,
    // -------------------------------------------------------------------------
    // Shape
    // -------------------------------------------------------------------------
    shape: {
      borderRadius: borderRadius,
    },

    // -------------------------------------------------------------------------
    // Palette
    // -------------------------------------------------------------------------
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      background: {
        default: other.pageBackground,
        paper: other.component1,
      },
      primary: {
        main: primary.main,
        dark: primary.dark,
        light: primary.light,
      },
      text: {
        primary: text.highContrast,
        secondary: text.mediumContrast,
        disabled: text.lowContrast,
      },
      success: {
        main: success.main,
        dark: success.dark,
        light: success.light,
      },
      warning: {
        main: warning.main,
        dark: warning.dark,
        light: warning.light,
      },
      error: {
        main: error.main,
        dark: error.dark,
        light: error.light,
      },
      info: {
        main: info.main,
        dark: info.dark,
        light: info.light,
      },
      other: {
        component1: other.component1,
        component2: other.component2,
        component3: other.component3,
        pageBackground: other.pageBackground,
        plainWhite: other.plainWhite,
        outlines: other.outlines,
        disabled: other.disabled,
        darkBlueGradient: other.darkBlueGradient,
        linearWhiteGradient: other.linearWhiteGradient,
      },
    },

    // -------------------------------------------------------------------------
    // Typography
    // -------------------------------------------------------------------------
    typography: {
      ...typographyStyles,
      // Default font-family
      body1: typographyStyles['body/regular'],
      body2: typographyStyles['body/regular-bold'],
      button: typographyStyles['btn-label/regular-bold'],
      caption: typographyStyles['body/extra-small'],
      overline: typographyStyles['body/extra-small-bold'],
      subtitle1: typographyStyles['body/small'],
      subtitle2: typographyStyles['body/small-bold'],
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            '&.MuiAlert-outlined': {
              alignItems: 'center',
              borderRadius: '0px',
              '&.MuiAlert-outlinedInfo': {
                backgroundColor: info.light,
                border: `1px solid ${info.dark}`,
                '&, & .MuiAlert-icon': {
                  color: info.dark,
                },
              },
              '&.MuiAlert-outlinedError': {
                backgroundColor: error.light,
                border: `1px solid ${error.dark}`,
                '&, & .MuiAlert-icon': {
                  color: error.dark,
                },
              },
              '&.MuiAlert-outlinedWarning': {
                backgroundColor: warning.light,
                border: `1px solid ${warning.dark}`,
                '&, & .MuiAlert-icon': {
                  color: warning.main,
                },
              },
              '&.MuiAlert-outlinedSuccess': {
                backgroundColor: success.light,
                border: `1px solid ${success.dark}`,

                '&, & .MuiAlert-icon': {
                  color: success.dark,
                },
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: other.component1,
            borderBottomStyle: 'none',
            borderBottomWidth: 1,
            borderBottomColor: other.outlines,
          },
        },
      },
      MuiAutocomplete: {
        defaultProps: {
          size: 'small',
        },
      },
      MuiAvatar: {
        styleOverrides: {
          circular: {
            ...typographyStyles['btn-label/small-bold'],
            color: other.plainWhite,
            background: other.darkBlueGradient,
            border: `1px solid ${other.outlines}`,
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            ...typographyStyles['btn-label/small-light'],
          },
          li: {
            color: text.mediumContrast,
            '&:hover': {
              color: text.highContrast,
            },
            p: {
              ...typographyStyles['btn-label/small-light'],
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'capitalize',
            padding: '11px 8px',
            borderRadius: '4px',
            '&.MuiButton-sizeSmall': {
              height: 32,
              ...typographyStyles['btn-label/small-bold'],
            },
            '&.MuiButton-sizeMedium': {
              height: 46,
              ...typographyStyles['btn-label/small-bold'],
            },
            '&.MuiButton-sizeLarge': {
              height: 64,
              ...typographyStyles['btn-label/small-bold'],
            },
            '&.MuiButton-contained': {
              '&.MuiButton-containedPrimary': {
                boxShadow: 'none',
                backgroundImage: other.darkBlueGradient,
                '&:hover:not(.Mui-disable)': {
                  background: primary.dark,
                },
                '&.Mui-disabled': {
                  background: other.disabled,
                  color: other.plainWhite,
                  '&.MuiLoadingButton-loading': {
                    color: text.mediumContrast,
                  },
                },
                '&.valuation': {
                  background: other.linearWhiteGradient,
                  color: text.highContrast,
                  minHeight: '64px',
                  minWidth: '350px',
                  justifyContent: 'space-between',
                  padding: '16px 24px',
                  '&:hover': {
                    background: other.component1,
                    '& > div': {
                      background: other.component2,
                    },
                  },
                  '&.MuiLoadingButton-root': {
                    justifyContent: 'flex-start',
                  },
                },
              },
              '&.MuiButton-containedSecondary': {
                backgroundColor: other.component1,
                boxShadow: 'none',
                color: primary.main,
                outline: `1px solid ${other.outlines}`,
                '&:hover:not(.Mui-disabled)': {
                  boxShadow: 'none',
                  outline: `1px solid ${primary.main}`,
                  backgroundColor: other.component3,
                },
                '&.Mui-disabled': {
                  backgroundColor: other.disabled,
                  color: other.plainWhite,
                  outline: `1px solid ${other.outlines}`,
                  '&.MuiLoadingButton-loading': {
                    backgroundColor: other.component3,
                    color: text.mediumContrast,
                    outline: `1px solid ${other.outlines}`,
                  },
                },
              },
              '&.MuiButton-containedError': {
                backgroundColor: error.light,
                boxShadow: 'none',
                color: error.dark,
                '&:hover:not(.Mui-disabled)': {
                  backgroundColor: error.light,
                  boxShadow: 'none',
                  outline: `1px solid ${error.dark}`,
                },
                '&.Mui-disabled': {
                  backgroundColor: other.plainWhite,
                  color: other.disabled,
                  outline: `1px solid ${other.disabled}`,
                  '&.MuiLoadingButton-loading': {
                    color: text.mediumContrast,
                    outline: `1px solid ${other.disabled}`,
                  },
                },
              },
            },
            '&.MuiButton-text': {
              ...typographyStyles['btn-label/small-bold'],
              padding: '0px',
              height: '14px',
              '&.MuiButton-textPrimary': {
                color: primary.main,
                '&:hover:not(.Mui-disabled)': {
                  color: primary.light,
                  backgroundColor: 'transparent',
                },
                '&.Mui-disabled': {
                  color: other.disabled,
                  '&.MuiLoadingButton-loading': {
                    color: text.mediumContrast,
                  },
                },
              },
              '&:not(.MuiLoadingButton-loading)': {
                gap: '4px',
              },
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            '&.MuiLoadingButton-root': {
              '& .MuiLoadingButton-loadingIndicator': {
                color: 'inherit',
                position: 'initial',
              },
            },
            '&.MuiTab-root': {
              minHeight: '100%',
              '&.Mui-selected': {
                backgroundColor: other.component3,
              },
              '&:hover': {
                color: primary.light,
              },
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            boxShadow: 'none',
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          focusHighlight: {
            background: 'transparent',
          },
        },
      },
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: '12px 16px;',
            alignSelf: 'stretch',
            maxHeight: '46px',
            alignItems: 'center',
            '&.subStep': {
              padding: '8px 16px 8px 45px',
            },
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            '&, & + .MuiTypography-root': {
              color: text.highContrast,
              ...typographyStyles['body/small'],
            },
            '&.Mui-checked': {
              '&, & + .MuiTypography-root': {
                color: primary.main,
              },
            },
            '&.Mui-disabled': {
              '&, & + .MuiTypography-root': {
                color: other.disabled,
              },
            },
            '&:hover': {
              '&, & + .MuiTypography-root': {
                color: primary.main,
                background: 'unset',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            ...typographyStyles['btn-label/extra-small'],
            textTransform: 'uppercase',
            borderRadius: '1px',
            height: '24px',
            '& .MuiChip-label': {
              padding: '0px 6px',
            },
          },
          colorInfo: {
            backgroundColor: info.light,
            color: info.dark,
          },
          colorSuccess: {
            backgroundColor: success.light,
            color: success.dark,
          },
          colorWarning: {
            backgroundColor: warning.light,
            color: warning.dark,
          },
          colorError: {
            backgroundColor: error.light,
            color: error.dark,
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            marginTop: '16px',
            marginBottom: '68px', // WizardSubStepper height
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: '24px',
            borderRadius: '1px',
            gap: '24px',
            boxShadow: '4px 5px 8px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            justifyContent: 'flex-end',
            padding: 0,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            [defaultTheme.breakpoints.down('sm')]: {
              justifyContent: 'center',
            },
            [defaultTheme.breakpoints.up('md')]: {
              width: '400px',
              justifyContent: 'center',
            },
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
            paddingTop: '1px !important',
            '&, & + .MuiTypography-root': {
              color: text.mediumContrast,
            },
            '& .imageCarouselNavButtonsWrapper': {
              '& > button': {
                backgroundColor: background.paper,
              },
              '&:hover > button, & > button:hover': {
                backgroundColor: `${background.paper} !important`,
                opacity: '1 !important',
                filter: 'unset',
              },
            },
            '& > .MuiBox-root': {
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            ...typographyStyles['h6-bold'],
            '& > .MuiIconButton-root': {
              color: text.mediumContrast,
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            my: 2,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: other.darkBlueGradient,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            transition: '0.2s linear',
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            width: '100%',
            marginTop: '10px',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            ...typographyStyles['btn-label/small-light'],
            display: 'flex',
            '&.MuiInputLabel-root': {
              marginLeft: '3px',
            },
            '&.label-invisible': {
              display: 'none',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: primary.main,
            '&.MuiIconButton-sizeMedium': {
              width: 30,
              height: 30,
              borderRadius: '4px',
              padding: '5px',
            },
            '&:hover': {
              '&:not(.Mui-disabled)&:not(.hidden-button)': {
                '&:hover:not(.Mui-disabled)': {
                  backgroundColor: other.component3,
                  boxShadow: 'none',
                  outline: `1px solid ${primary.main}`,
                },
              },
            },
            '&.Mui-disabled': { color: other.disabled },
          },
        },
      },
      MuiInputAdornment: {
        styleOverrides: {
          root: {
            color: text.mediumContrast,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            '&.MuiIconButton-sizeMedium': {
              width: 30,
              height: 30,
              borderRadius: '4px',
              padding: '5px',
            },
          },
          sizeSmall: {
            marginTop: '0px !important',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            '&, & + .MuiTypography-root': {
              ...typographyStyles['btn-label/small-light'],
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: text.highContrast,
            fontSize: '17px',
          },
        },
      },
      MuiListItemText: {
        defaultProps: {
          primaryTypographyProps: {
            variant: 'btn-label/small-light',
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          PaperProps: {
            style: {
              alignSelf: 'center',
              boxShadow: 'none',
              borderRadius: 0,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            ...typographyStyles['btn-label/small-light'],
            backgroundColor: background.paper,
            padding: '8px 16px',
            borderRadius: '4px',
            margin: '8px',
            '&:hover': {
              backgroundColor: other.component3,
            },
            '&.Mui-selected': {
              backgroundColor: other.component3,
            },
            '&.Mui-focusVisible': {
              backgroundColor: other.component3,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            [`&:hover:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: other.outlines,
            },
            [`&.Mui-focused:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: other.outlines,
            },
            [`&.Mui-disabled .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: other.outlines,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // Styling for the Autocomplete popup (renders outside the Autocomplete element)
            '&.MuiAutocomplete-paper': {
              ...typographyStyles['btn-label/small-light'],
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            '&, & + .MuiTypography-root': {
              color: text.highContrast,
              ...typographyStyles['btn-label/small-light'],
            },
            '&.Mui-checked': {
              '&, & + .MuiTypography-root': {
                color: primary.main,
              },
            },
            '&.Mui-disabled': {
              '&, & + .MuiTypography-root': {
                color: other.disabled,
              },
            },
            '&:hover': {
              '&, & + .MuiTypography-root': {
                color: primary.main,
                background: 'unset',
              },
            },
          },
        },
      },
      // MuiSelect: {
      //   styleOverrides: {
      //     root: {
      //       [`.${outlinedInputClasses.notchedOutline}`]: {
      //         borderColor: 'transparent',
      //         ...typographyStyles['btn-label/small-light'],
      //       },
      //       [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
      //         borderColor: 'transparent',
      //         ...typographyStyles['btn-label/small-light'],
      //       },
      //       [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
      //         borderColor: 'transparent',
      //         ...typographyStyles['btn-label/small-light'],
      //       },
      //       [`&.Mui-disabled .${outlinedInputClasses.notchedOutline}`]: {
      //         borderColor: 'transparent',
      //         ...typographyStyles['btn-label/small-light'],
      //       },
      //       ':hover:not(.Mui-disabled, .Mui-error):before': {
      //         border: 'none',
      //       },
      //       ':after': {
      //         border: 'none',
      //       },
      //       ':before': {
      //         border: 'none',
      //       },
      //     },
      //     select: {
      //       padding: '2px',
      //       ...typographyStyles['btn-label/small-light'],
      //       ':focus': {
      //         backgroundColor: 'transparent',
      //       },
      //     },
      //   },
      // },

      MuiSvgIcon: {
        styleOverrides: {
          root: {
            '.RoundedIcon &': {
              color: text.mediumContrast,
              width: '20px',
            },
            '.MuiIconButton-root &': {
              '&:hover': {
                '&:not(.Mui-disabled)': {
                  fill: primary.dark,
                },
              },
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            ...typographyStyles['btn-label/small-bold'],
            color: primary.main,
            textTransform: 'capitalize',
            '&.Mui-selected': {
              background: other.plainWhite,
            },
            '&:hover': {
              ...typographyStyles['btn-label/small-bold'],
              color: primary.main,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            '.MuiOutlinedInput-root': {
              ...typographyStyles['btn-label/small-light'],
              borderRadius: '0',
              padding: '0 13px',
              fieldset: {
                border: `1px solid ${other.outlines}`,
              },
              background: other.component1,
            },
            '.MuiOutlinedInput-input': {
              padding: '8px 13px',
              paddingLeft: '0',
              ...typographyStyles['btn-label/small-light'],
              color: text.highContrast,
              '&::placeholder': {
                color: text.mediumContrast,
                opacity: 1,
              },
            },
            '.MuiInputLabel-root, .MuiInputAdornment-root .MuiTypography-root': {
              ...typographyStyles['btn-label/small-light'],
              color: text.mediumContrast,
            },
            '.MuiIconButton-root': {
              color: text.mediumContrast,
            },
            '&.Mui-disabled:not(label)': {
              background: other.disabled,
            },
            '.Mui-disabled': {
              color: text.lowContrast,
              '.MuiInputAdornment-root p, .MuiButtonBase-root': {
                color: text.lowContrast,
              },
            },
            '& label.Mui-focused': {
              color: text.mediumContrast,
            },
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            ...typographyStyles['btn-label/small-light'],
            color: text.highContrast,
            '.MuiIconButton-root': {
              color: text.highContrast,
            },
            minHeight: '52px',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            '&.activeWizardStep': {
              color: other.textOnDarkHighContrast,
              ...typographyStyles['btn-label/small-bold'],
            },
            '&.inactiveWizardStep': {
              color: other.textOnDarkMediumContrast,
              ...typographyStyles['btn-label/small-bold'],
            },
          },
        },
      },
    },
  });

// export default createAppTheme;

export const customTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        background: {
          default: other.pageBackground,
          paper: other.component1,
        },
        primary: {
          main: primary.main,
          dark: primary.dark,
          light: primary.light,
        },
        text: {
          primary: text.highContrast,
          secondary: text.mediumContrast,
          disabled: text.lowContrast,
        },
        success: {
          main: success.main,
          dark: success.dark,
          light: success.light,
        },
        warning: {
          main: warning.main,
          dark: warning.dark,
          light: warning.light,
        },
        error: {
          main: error.main,
          dark: error.dark,
          light: error.light,
        },
        info: {
          main: info.main,
          dark: info.dark,
          light: info.light,
        },
        other: {
          component1: other.component1,
          component2: other.component2,
          component3: other.component3,
          pageBackground: other.pageBackground,
          plainWhite: other.plainWhite,
          outlines: other.outlines,
          disabled: other.disabled,
          darkBlueGradient: other.darkBlueGradient,
          linearWhiteGradient: other.linearWhiteGradient,
        },
      },
    },
  },
});
